import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChartBar, FaImage, FaDollarSign, FaUsers, FaSync, FaChevronLeft, FaChevronRight, FaPalette, FaGamepad, FaArrowLeft, FaEye, FaShoppingCart, FaCrown, FaCog, FaGift, FaTrash, FaVideo, FaCamera } from 'react-icons/fa';
import { format, formatDistanceToNow } from 'date-fns';
import { Link, useParams, useNavigate, useLocation, Routes, Route } from 'react-router-dom';
import ImageCarousel from '../components/ImageCarousel';
import ImageCard from '../components/ImageCard';
import UserDetailView from '../components/UserDetailView';
import SpiceRackAdmin from './SpiceRackAdmin';
import LogoDemo from './LogoDemo';
import WormieEdit from './WormieEdit';
import InvisibleLooper from './InvisibleLooper';
import MessageScreenGrab from './MessageScreenGrab';

import GameManagement from './GameManagement';
import GameDemo from './GameDemo';
import BannerManagement from './BannerManagement';

const AdminDashboard = () => {
  console.log('[RETRO-ADMIN] 🏗️ AdminDashboard component rendering');
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  console.log('[RETRO-ADMIN] 📍 Current location:', location.pathname);
  
  // Extract userId from URL path manually since useParams() doesn't work with wildcard routes
  const getUserIdFromPath = () => {
    const path = location.pathname;
    const userMatch = path.match(/\/admin\/users\/([^\/]+)/);
    return userMatch ? userMatch[1] : null;
  };

  // Extract gameId from URL path for game demos
  const getGameIdFromPath = () => {
    const path = location.pathname;
    const gameMatch = path.match(/\/admin\/game-demo\/([^\/]+)/);
    return gameMatch ? gameMatch[1] : null;
  };
  
  const userId = getUserIdFromPath();
  const gameId = getGameIdFromPath();
  
  // Determine current view from URL path
  const getCurrentView = () => {
    const path = location.pathname;
    console.log('[RETRO-ADMIN] 🛣️ getCurrentView - path:', path);
    if (path.includes('/users/') && userId) return 'user-detail';
    if (path.includes('/users')) return 'users';
    if (path.includes('/spicerack')) return 'spicerack';
    if (path.includes('/logo-creator')) return 'logo-creator';
    if (path.includes('/wormiedit')) return 'wormiedit';
    if (path.includes('/invisible-looper')) return 'invisible-looper';
    if (path.includes('/message-screengrab')) return 'message-screengrab';
    if (path.includes('/banner-management')) return 'banner-management';

    if (path.includes('/game-demo/')) {
      console.log('[RETRO-ADMIN] 🎮 Detected game-demo view');
      return 'game-demo';
    }
    if (path.includes('/game-management')) {
      console.log('[RETRO-ADMIN] 🎮 Detected game-management view');
      return 'game-management';
    }
    return 'overview';
  };
  
  const [view, setView] = useState(getCurrentView());
  const [selectedUserId, setSelectedUserId] = useState(userId || null);
  
  console.log('[RETRO-ADMIN] 🔍 Current view state:', view);
  
  // Update view when location changes
  useEffect(() => {
    const newView = getCurrentView();
    console.log('[RETRO-ADMIN] 🔄 Location effect - new view:', newView);
    setView(newView);
  }, [location.pathname]);
  const [stats, setStats] = useState(null);
  const [recentImages, setRecentImages] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hoveredImage, setHoveredImage] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [userPagination, setUserPagination] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [giftTokens, setGiftTokens] = useState({ amount: '', reason: '', isGifting: false });
  const [bulkGiftTokens, setBulkGiftTokens] = useState({ amount: '', reason: '', isGifting: false });
  const [selectedGameForDemo, setSelectedGameForDemo] = useState(null);
  const [stripeData, setStripeData] = useState(null);
  const [isLoadingStripe, setIsLoadingStripe] = useState(false);


  const fetchData = async (page = 1) => {
    if (!isAuthenticated) return;

    try {
      setIsRefreshing(true);
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: "retro-api",
          scope: "openid profile email"
        }
      });
      
      // Fetch stats
      const statsResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/stats`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!statsResponse.ok) {
        throw new Error('Failed to fetch stats');
      }

      const statsData = await statsResponse.json();
      setStats(statsData);

      // Fetch recent images
      setIsLoadingMore(true);
      const imagesResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/recent-images?page=${page}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!imagesResponse.ok) {
        throw new Error('Failed to fetch images');
      }

      const data = await imagesResponse.json();
      if (page === 1) {
        setRecentImages(data.images);
      } else {
        setRecentImages(prev => [...prev, ...data.images]);
      }
      setPagination(data.pagination);
    } catch (error) {
      console.error('[RETRO-ADMIN] Error:', error.message);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
      setIsLoadingMore(false);
    }
  };

  const fetchUsers = async (page = 1) => {
    if (!isAuthenticated) return;

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: "retro-api",
          scope: "openid profile email"
        }
      });

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/users?page=${page}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      setUsers(data.users);
      setUserPagination(data.pagination);
    } catch (error) {
      console.error('[RETRO-ADMIN] Users Error:', error.message);
    }
  };

  const fetchUserDetails = async (userId) => {
    if (!isAuthenticated) return;

    try {
      setIsLoading(true);
      setStripeData(null); // Reset Stripe data when fetching new user
      console.log('[RETRO-ADMIN] 👤 Fetching user details:', {
        userId,
        decodedUserId: decodeURIComponent(userId),
        timestamp: new Date().toISOString()
      });
      
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: "retro-api",
          scope: "openid profile email"
        }
      });

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/users/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user details');
      }

      const userData = await response.json();
      console.log('[RETRO-ADMIN] ✅ User details loaded:', {
        userId,
        userEmail: userData.email,
        imagesCount: userData.images?.length,
        timestamp: new Date().toISOString()
      });
      setSelectedUser(userData);
    } catch (error) {
      console.error('[RETRO-ADMIN] User Details Error:', error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStripeStatus = async (userId) => {
    if (!isAuthenticated) return;

    try {
      setIsLoadingStripe(true);
      console.log('[RETRO-ADMIN] 💳 Fetching Stripe status:', {
        userId,
        timestamp: new Date().toISOString()
      });
      
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: "retro-api",
          scope: "openid profile email"
        }
      });

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/users/${userId}/stripe-status`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch Stripe status');
      }

      const stripeStatusData = await response.json();
      console.log('[RETRO-ADMIN] ✅ Stripe status loaded:', {
        userId,
        hasCustomer: stripeStatusData.hasStripeCustomer,
        subscriptionsCount: stripeStatusData.subscriptions?.length || 0,
        chargesCount: stripeStatusData.charges?.length || 0,
        timestamp: new Date().toISOString()
      });
      setStripeData(stripeStatusData);
    } catch (error) {
      console.error('[RETRO-ADMIN] Stripe Status Error:', error.message);
      setStripeData({ error: error.message });
    } finally {
      setIsLoadingStripe(false);
    }
  };

  // Sync view state with URL changes
  useEffect(() => {
    const newView = getCurrentView();
    console.log('[RETRO-ADMIN] 🛣️ Route change detected:', {
      path: location.pathname,
      userId,
      newView,
      timestamp: new Date().toISOString()
    });
    setView(newView);
    setSelectedUserId(userId || null);
  }, [location.pathname, userId]);

  useEffect(() => {
    if (view === 'overview') {
      fetchData();
    } else if (view === 'users') {
      fetchUsers();
    } else if (view === 'user-detail' && selectedUserId) {
      fetchUserDetails(selectedUserId);
      fetchStripeStatus(selectedUserId);
    } else {
      // SpiceRack, Logo Creator, WormieEdit, Retro Arcade, and Game Demo views don't need data fetching
      console.log('[RETRO-ADMIN] 🎯 Setting loading to false for view:', view);
      setIsLoading(false);
    }
  }, [isAuthenticated, getAccessTokenSilently, view, selectedUserId]);

  const loadMore = () => {
    if (pagination?.hasMore && !isLoadingMore) {
      fetchData(currentPage + 1);
      setCurrentPage(prev => prev + 1);
    }
  };

  const handleImageClick = (image) => {
    const images = view === 'user-detail' ? selectedUser?.images : recentImages;
    const index = images?.findIndex(img => img.id === image.id);
    if (index !== -1) {
      setSelectedImageIndex(index);
    }
  };

  const navigateToUsers = () => {
    navigate('/admin/users');
  };

  const navigateToUserDetail = (userId) => {
    navigate(`/admin/users/${userId}`);
  };

  const navigateToSpiceRack = () => {
    navigate('/admin/spicerack');
  };

  const navigateToLogoCreator = () => {
    navigate('/admin/logo-creator');
  };

  const navigateToWormieEdit = () => {
    navigate('/admin/wormiedit');
  };

  const navigateToInvisibleLooper = () => {
    navigate('/admin/invisible-looper');
  };

  const navigateToMessageScreenGrab = () => {
    navigate('/admin/message-screengrab');
  };

  const navigateToBannerManagement = () => {
    navigate('/admin/banner-management');
  };

  const navigateToGameManagement = () => {
    navigate('/admin/game-management');
  };

  const navigateToGameDemo = (game) => {
    console.log('[RETRO-ADMIN] 🎮 Navigating to game demo:', game);
    setSelectedGameForDemo(game);
    navigate(`/admin/game-demo/${game.id}`);
  };

  const navigateBack = () => {
    if (view === 'user-detail') {
      navigate('/admin/users');
      setSelectedUser(null);
    } else if (view === 'game-demo') {
      navigate('/admin/game-management');
    } else if (view === 'users' || view === 'spicerack' || view === 'logo-creator' || view === 'wormiedit' || view === 'invisible-looper' || view === 'message-screengrab' || view === 'banner-management' || view === 'retro-arcade' || view === 'game-management') {
      navigate('/admin');
    }
  };

  const handleGiftTokens = async () => {
    if (!giftTokens.amount || giftTokens.amount <= 0) {
      return;
    }

    try {
      setGiftTokens(prev => ({ ...prev, isGifting: true }));
      
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: "retro-api",
          scope: "openid profile email"
        }
      });

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/users/${selectedUserId}/gift-tokens`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: parseInt(giftTokens.amount),
          reason: giftTokens.reason || 'Admin gift'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to gift tokens');
      }

      const result = await response.json();
      
      // Refresh user details to show updated balance and transaction history
      await fetchUserDetails(selectedUserId);
      
      // Reset form
      setGiftTokens({ amount: '', reason: '', isGifting: false });
      
      console.log('[RETRO-ADMIN] ✅ Tokens gifted successfully:', result);
      
    } catch (error) {
      console.error('[RETRO-ADMIN] ❌ Error gifting tokens:', error);
    } finally {
      setGiftTokens(prev => ({ ...prev, isGifting: false }));
    }
  };

  const handleBulkGiftTokens = async () => {
    if (!bulkGiftTokens.amount || bulkGiftTokens.amount <= 0) {
      return;
    }

    // Extra confirmation for bulk gifts
    const userCount = users?.length || 0;
    const totalTokens = userCount * parseInt(bulkGiftTokens.amount);
    
    if (!window.confirm(
      `Are you sure you want to gift ${bulkGiftTokens.amount} tokens to ALL ${userCount} users?\n\n` +
      `This will distribute ${totalTokens} total tokens.\n\n` +
      `Reason: ${bulkGiftTokens.reason || 'Admin bulk gift'}`
    )) {
      return;
    }

    try {
      setBulkGiftTokens(prev => ({ ...prev, isGifting: true }));
      
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: "retro-api",
          scope: "openid profile email"
        }
      });

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/users/gift-tokens-to-all`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: parseInt(bulkGiftTokens.amount),
          reason: bulkGiftTokens.reason || 'Admin bulk gift'
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to gift tokens to all users');
      }

      const result = await response.json();
      
      // Show success message with details
      alert(
        `🎉 Success!\n\n` +
        `${result.message}\n\n` +
        `Details:\n` +
        `• Users updated: ${result.details.usersUpdated}\n` +
        `• Tokens per user: ${result.details.tokensPerUser}\n` +
        `• Total tokens gifted: ${result.details.totalTokensGifted}\n` +
        `• Reason: ${result.details.reason}`
      );
      
      // Refresh data to show updated counts
      await fetchData();
      
      // Reset form
      setBulkGiftTokens({ amount: '', reason: '', isGifting: false });
      
      console.log('[RETRO-ADMIN] ✅ Bulk tokens gifted successfully:', result);
      
    } catch (error) {
      console.error('[RETRO-ADMIN] ❌ Error bulk gifting tokens:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setBulkGiftTokens(prev => ({ ...prev, isGifting: false }));
    }
  };



  if (isLoading && view !== 'overview') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <motion.div 
          className="w-8 h-8 border-2 border-black/20 border-t-black/40 rounded-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        />
      </div>
    );
  }

  // User List View
  if (view === 'users') {
    return (
      <div className="space-y-8 max-w-[920px] mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <motion.button
              onClick={navigateBack}
              className="flex items-center gap-2 px-4 py-2 bg-black/[0.03] hover:bg-black/[0.05] rounded-lg font-['VT323'] text-[18px] text-black/60 hover:text-black/80 transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FaArrowLeft className="w-4 h-4" />
              Back to Admin
            </motion.button>
            <h1 className="text-2xl font-['VT323'] text-black/60">Users</h1>
          </div>
        </div>

        {/* Users Grid */}
        <div className="bg-white/90 backdrop-blur-xl rounded-xl p-6 shadow-sm border border-black/[0.08]">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-['VT323'] text-xl text-black/40">All Users</h2>
            {userPagination && (
              <div className="text-sm font-['VT323'] text-black/40">
                Showing {users.length} of {userPagination.totalCount} users
              </div>
            )}
          </div>
          
          <div className="space-y-3">
            {users.map(user => (
              <motion.div
                key={user.id}
                onClick={() => navigateToUserDetail(user.id)}
                className="flex items-center justify-between p-4 bg-black/[0.02] hover:bg-black/[0.05] rounded-lg cursor-pointer transition-colors"
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="font-['VT323'] text-[18px] text-black/70 flex items-center gap-2">
                      {user.email || 'No email'}
                      {user.isFounder && (
                        <FaCrown className="text-yellow-500 w-4 h-4" title="Founder" />
                      )}
                    </h3>
                    {user.subscriptionTier && user.subscriptionTier !== 'free' && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-['VT323'] rounded">
                        {user.subscriptionTier.toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-sm font-['VT323'] text-black/50">
                    {user._count.images} images • {user._count.tokenPurchases} purchases • {user.tokenBalance} tokens
                  </div>
                  <div className="text-xs font-['VT323'] text-black/40">
                    Joined {format(new Date(user.createdAt), 'MMM d, yyyy')}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <FaEye className="w-4 h-4 text-black/40" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // User Detail View
  if (view === 'user-detail' && selectedUser) {
    return (
      <UserDetailView
        selectedUser={selectedUser}
        giftTokens={giftTokens}
        setGiftTokens={setGiftTokens}
        handleGiftTokens={handleGiftTokens}
        stripeData={stripeData}
        isLoadingStripe={isLoadingStripe}
        fetchStripeStatus={fetchStripeStatus}
        selectedUserId={selectedUserId}
        handleImageClick={handleImageClick}
        navigateBack={navigateBack}
        getAccessTokenSilently={getAccessTokenSilently}
        fetchUserDetails={fetchUserDetails}
      />
    );
  }

  // SpiceRack Admin View
  if (view === 'spicerack') {
    return (
      <div className="space-y-8 max-w-[920px] mx-auto">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <motion.button
              onClick={navigateBack}
              className="flex items-center gap-2 px-4 py-2 bg-black/[0.03] hover:bg-black/[0.05] rounded-lg font-['VT323'] text-[18px] text-black/60 hover:text-black/80 transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FaArrowLeft className="w-4 h-4" />
              Back to Admin
            </motion.button>
            <h1 className="text-2xl font-['VT323'] text-black/60">SpiceRack Admin</h1>
          </div>
        </div>
        <SpiceRackAdmin />
      </div>
    );
  }

  // Logo Creator View
  if (view === 'logo-creator') {
    return (
      <div className="space-y-8 max-w-[920px] mx-auto">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <motion.button
              onClick={navigateBack}
              className="flex items-center gap-2 px-4 py-2 bg-black/[0.03] hover:bg-black/[0.05] rounded-lg font-['VT323'] text-[18px] text-black/60 hover:text-black/80 transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FaArrowLeft className="w-4 h-4" />
              Back to Admin
            </motion.button>
            <h1 className="text-2xl font-['VT323'] text-black/60">Logo Creator</h1>
          </div>
        </div>
        <LogoDemo />
      </div>
    );
  }

  // WormieEdit View
  if (view === 'wormiedit') {
    return <WormieEdit onBack={navigateBack} />;
  }

  if (view === 'invisible-looper') {
    return <InvisibleLooper onBack={navigateBack} />;
  }

  if (view === 'message-screengrab') {
    return <MessageScreenGrab onBack={navigateBack} />;
  }

  // Banner Management View
  if (view === 'banner-management') {
    return <BannerManagement onBack={navigateBack} />;
  }

  // Game Management View
  if (view === 'game-management') {
    return (
      <div className="space-y-8 max-w-[920px] mx-auto">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <motion.button
              onClick={navigateBack}
              className="flex items-center gap-2 px-4 py-2 bg-black/[0.03] hover:bg-black/[0.05] rounded-lg font-['VT323'] text-[18px] text-black/60 hover:text-black/80 transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FaArrowLeft className="w-4 h-4" />
              Back to Admin
            </motion.button>
            <h1 className="text-2xl font-['VT323'] text-black/60">Game Management</h1>
          </div>
        </div>
        <GameManagement onBack={navigateBack} onPlayGame={navigateToGameDemo} />
      </div>
    );
  }

  // Game Demo View
  if (view === 'game-demo' && gameId) {
    return (
      <div className="space-y-8 max-w-[920px] mx-auto">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <motion.button
              onClick={navigateBack}
              className="flex items-center gap-2 px-4 py-2 bg-black/[0.03] hover:bg-black/[0.05] rounded-lg font-['VT323'] text-[18px] text-black/60 hover:text-black/80 transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FaArrowLeft className="w-4 h-4" />
              Back to Game Management
            </motion.button>
            <h1 className="text-2xl font-['VT323'] text-black/60">Game Demo</h1>
          </div>
        </div>
        <GameDemo gameId={gameId} onBack={navigateBack} />
      </div>
    );
  }

  // Main Overview Dashboard
  return (
    <div className="space-y-8 max-w-[920px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-['VT323'] text-black/60">Admin Dashboard</h1>
        <motion.button
          onClick={() => fetchData()}
          disabled={isRefreshing}
          className="flex items-center gap-2 px-4 py-2 bg-black/[0.03] hover:bg-black/[0.05] rounded-lg font-['VT323'] text-[18px] text-black/60 hover:text-black/80 transition-colors disabled:opacity-50"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <FaSync className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          Refresh
        </motion.button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <motion.div 
            className="bg-white/90 backdrop-blur-xl rounded-xl p-6 shadow-sm border border-black/[0.08]"
            whileHover={{ scale: 1.02 }}
          >
            <div className="flex items-center gap-3">
              <FaImage className="w-5 h-5 text-blue-500" />
              <h3 className="font-['VT323'] text-lg text-black/40">Total Images</h3>
            </div>
            <p className="mt-2 text-2xl font-['VT323'] text-black/70">{stats.totalImages}</p>
          </motion.div>

          <motion.div 
            className="bg-white/90 backdrop-blur-xl rounded-xl p-6 shadow-sm border border-black/[0.08]"
            whileHover={{ scale: 1.02 }}
          >
            <div className="flex items-center gap-3">
              <FaChartBar className="w-5 h-5 text-green-500" />
              <h3 className="font-['VT323'] text-lg text-black/40">Today</h3>
            </div>
            <p className="mt-2 text-2xl font-['VT323'] text-black/70">{stats.imagesToday}</p>
          </motion.div>

          <motion.div 
            className="bg-white/90 backdrop-blur-xl rounded-xl p-6 shadow-sm border border-black/[0.08]"
            whileHover={{ scale: 1.02 }}
          >
            <div className="flex items-center gap-3">
              <FaUsers className="w-5 h-5 text-purple-500" />
              <h3 className="font-['VT323'] text-lg text-black/40">Active Users</h3>
            </div>
            <p className="mt-2 text-2xl font-['VT323'] text-black/70">{stats.activeUsers}</p>
          </motion.div>

          <motion.div 
            className="bg-white/90 backdrop-blur-xl rounded-xl p-6 shadow-sm border border-black/[0.08]"
            whileHover={{ scale: 1.02 }}
          >
            <div className="flex items-center gap-3">
              <FaDollarSign className="w-5 h-5 text-yellow-500" />
              <h3 className="font-['VT323'] text-lg text-black/40">MRR</h3>
            </div>
            <p className="mt-2 text-2xl font-['VT323'] text-black/70">${stats.mrr}</p>
          </motion.div>
        </div>
      )}

      {/* Navigation Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        <motion.button
          onClick={navigateToUsers}
          className="bg-white/90 backdrop-blur-xl rounded-xl p-6 shadow-sm border border-black/[0.08] text-left hover:bg-black/[0.02] transition-colors"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="flex items-center gap-3">
            <FaUsers className="w-5 h-5 text-blue-500" />
            <h3 className="font-['VT323'] text-lg text-black/60">Users</h3>
          </div>
          <p className="mt-2 text-sm font-['VT323'] text-black/40">Manage user accounts</p>
        </motion.button>

        <motion.button
          onClick={navigateToSpiceRack}
          className="bg-white/90 backdrop-blur-xl rounded-xl p-6 shadow-sm border border-black/[0.08] text-left hover:bg-black/[0.02] transition-colors"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="flex items-center gap-3">
            <FaPalette className="w-5 h-5 text-purple-500" />
            <h3 className="font-['VT323'] text-lg text-black/60">SpiceRack</h3>
          </div>
          <p className="mt-2 text-sm font-['VT323'] text-black/40">Configure style options</p>
        </motion.button>

        <motion.button
          onClick={navigateToLogoCreator}
          className="bg-white/90 backdrop-blur-xl rounded-xl p-6 shadow-sm border border-black/[0.08] text-left hover:bg-black/[0.02] transition-colors"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="flex items-center gap-3">
            <FaCog className="w-5 h-5 text-green-500" />
            <h3 className="font-['VT323'] text-lg text-black/60">Logo Creator</h3>
          </div>
          <p className="mt-2 text-sm font-['VT323'] text-black/40">Design tools demo</p>
        </motion.button>

        <motion.button
          onClick={navigateToWormieEdit}
          className="bg-white/90 backdrop-blur-xl rounded-xl p-6 shadow-sm border border-black/[0.08] text-left hover:bg-black/[0.02] transition-colors"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="flex items-center gap-3">
            <FaVideo className="w-5 h-5 text-red-500" />
            <h3 className="font-['VT323'] text-lg text-black/60">WormieEdit</h3>
          </div>
          <p className="mt-2 text-sm font-['VT323'] text-black/40">Boomerang video creator</p>
        </motion.button>

        <motion.button
          onClick={navigateToInvisibleLooper}
          className="bg-white/90 backdrop-blur-xl rounded-xl p-6 shadow-sm border border-black/[0.08] text-left hover:bg-black/[0.02] transition-colors"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="flex items-center gap-3">
            <FaEye className="w-5 h-5 text-cyan-500" />
            <h3 className="font-['VT323'] text-lg text-black/60">Invisible Looper</h3>
          </div>
          <p className="mt-2 text-sm font-['VT323'] text-black/40">Seamless video loop creator</p>
        </motion.button>

        <motion.button
          onClick={navigateToMessageScreenGrab}
          className="bg-white/90 backdrop-blur-xl rounded-xl p-6 shadow-sm border border-black/[0.08] text-left hover:bg-black/[0.02] transition-colors"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="flex items-center gap-3">
            <FaCamera className="w-5 h-5 text-purple-500" />
            <h3 className="font-['VT323'] text-lg text-black/60">Message Screen Grab</h3>
          </div>
          <p className="mt-2 text-sm font-['VT323'] text-black/40">Cinematic iOS message conversations with parallax</p>
        </motion.button>

        <motion.button
          onClick={navigateToBannerManagement}
          className="bg-white/90 backdrop-blur-xl rounded-xl p-6 shadow-sm border border-black/[0.08] text-left hover:bg-black/[0.02] transition-colors"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="flex items-center gap-3">
            <FaGift className="w-5 h-5 text-pink-500" />
            <h3 className="font-['VT323'] text-lg text-black/60">Banner Management</h3>
          </div>
          <p className="mt-2 text-sm font-['VT323'] text-black/40">Create dismissable notification banners</p>
        </motion.button>

        <motion.button
          onClick={navigateToGameManagement}
          className="bg-white/90 backdrop-blur-xl rounded-xl p-6 shadow-sm border border-black/[0.08] text-left hover:bg-black/[0.02] transition-colors"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="flex items-center gap-3">
            <FaGamepad className="w-5 h-5 text-orange-500" />
            <h3 className="font-['VT323'] text-lg text-black/60">Game Management</h3>
          </div>
          <p className="mt-2 text-sm font-['VT323'] text-black/40">Configure retro games</p>
        </motion.button>
      </div>

      {/* Bulk Gift Tokens Section */}
      <div className="bg-white/90 backdrop-blur-xl rounded-xl p-6 shadow-sm border border-black/[0.08]">
        <div className="flex items-center gap-2 mb-4">
          <FaGift className="w-5 h-5 text-purple-500" />
          <h2 className="font-['VT323'] text-xl text-black/40">Gift Tokens to All Users</h2>
        </div>
        
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-yellow-400 rounded-full flex-shrink-0"></div>
            <p className="font-['VT323'] text-[14px] text-yellow-800">
              ⚠️ This will gift tokens to ALL {stats?.activeUsers || 0} users in the database. Use with caution!
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-['VT323'] text-black/60 mb-2">
              Tokens per User (Max: 1000)
            </label>
            <input
              type="number"
              min="1"
              max="1000"
              value={bulkGiftTokens.amount}
              onChange={(e) => setBulkGiftTokens(prev => ({ ...prev, amount: e.target.value }))}
              placeholder="e.g. 50"
              className="w-full px-4 py-3 bg-white/90 text-black border border-black/10 rounded-xl font-['VT323'] text-[16px] focus:outline-none focus:ring-2 focus:ring-[#00FF9F]/50 focus:bg-white hover:bg-white transition-all duration-200"
            />
            {bulkGiftTokens.amount && (
              <p className="mt-1 text-xs font-['VT323'] text-black/50">
                Total: {parseInt(bulkGiftTokens.amount || 0) * (stats?.activeUsers || 0)} tokens
              </p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-['VT323'] text-black/60 mb-2">
              Reason (Optional)
            </label>
            <input
              type="text"
              value={bulkGiftTokens.reason}
              onChange={(e) => setBulkGiftTokens(prev => ({ ...prev, reason: e.target.value }))}
              placeholder="e.g. Welcome bonus, Holiday gift"
              className="w-full px-4 py-3 bg-white/90 text-black border border-black/10 rounded-xl font-['VT323'] text-[16px] focus:outline-none focus:ring-2 focus:ring-[#00FF9F]/50 focus:bg-white hover:bg-white transition-all duration-200"
            />
          </div>
          
          <div className="flex items-end">
            <motion.button
              onClick={handleBulkGiftTokens}
              disabled={!bulkGiftTokens.amount || bulkGiftTokens.amount <= 0 || bulkGiftTokens.isGifting}
              className="w-full px-6 py-3 bg-purple-500 hover:bg-purple-600 text-white font-['VT323'] text-[18px] rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FaGift className="w-4 h-4" />
              {bulkGiftTokens.isGifting ? 'Gifting...' : 'Gift to All Users'}
            </motion.button>
          </div>
        </div>
      </div>

      {/* Recent Images */}
      {recentImages.length > 0 && (
        <div className="bg-white/90 backdrop-blur-xl rounded-xl p-6 shadow-sm border border-black/[0.08]">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-['VT323'] text-xl text-black/40">Recent Images</h2>
            {pagination && (
              <div className="text-sm font-['VT323'] text-black/40">
                Showing {recentImages.length} of {pagination.totalCount} images
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {recentImages.map(image => (
              <ImageCard
                key={image.id}
                image={image}
                onClick={() => handleImageClick(image)}
                onImageLoaded={() => {}}
                isFavorited={false}
                isAnimated={true}
                showFavoriteCount={false}
                hideHoverButtons={false}
                isAdminView={true}
              />
            ))}
          </div>

          {pagination?.hasMore && (
            <div className="flex justify-center mt-6">
              <motion.button
                onClick={loadMore}
                disabled={isLoadingMore}
                className="px-6 py-3 bg-black/[0.03] hover:bg-black/[0.05] rounded-lg font-['VT323'] text-[18px] text-black/60 hover:text-black/80 transition-colors disabled:opacity-50"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {isLoadingMore ? 'Loading...' : 'Load More'}
              </motion.button>
            </div>
          )}
        </div>
      )}

      {/* Image Carousel */}
      {selectedImageIndex !== null && (
        <ImageCarousel
          images={recentImages}
          currentIndex={selectedImageIndex}
          onClose={() => setSelectedImageIndex(null)}
          onImageChange={(index) => setSelectedImageIndex(index)}
          isAdminView={true}
        />
      )}
    </div>
  );
}

export default AdminDashboard; 
